import { takeEvery, takeLatest, fork, put, call, select, delay } from 'redux-saga/effects';
import {
    InboundRoute,
    InboundPayload,
    InboundRouteFilterType as FilterPresets,
    InboundRouteActionType as ActionPresets,
} from '@dev/ee-api/ee-api-models';
import { ShowToast, HideToast } from '@api/store/toast/actions';
import {
    LoadInboundRoutesSuccess,
    InboundError,
    AddNewInboundSuccess,
    SetSingleInbound,
    SetInputError,
    UpdateInboundSuccess,
    CleanSingleInbound,
} from '@api/store/inboundRoutes/actions';
import {
    AddInboundRouteAction,
    ValidateInboundInputAction,
    SetInboundOrderAction,
    DeleteSingleInboundAction,
    UpdateInboundAction,
} from '@api/store/inboundRoutes/types';
import { loadInboundRoutes, addNewInboundRoute, updateInboundOrder, deleteInbound, updateInbound } from '@api/services/ElasticApi';
import { ApplicationState as State } from '@api/store';
import isEmail from 'validator/lib/isEmail';
import isURL from 'validator/lib/isURL';
import { Logger } from '@api/services/errors/appLogger';

const getUserInboundRoutes = (state: State): InboundRoute[] => state.inbound.routes;
const getSingleInbound = (state: State): InboundPayload => state.inbound.singleInbound;
const getInboundErrors = (state: State): string[] => state.inbound.errors || [];
function* fetchInbounds(): any {
    try {
        const inbounds = yield call(loadInboundRoutes);
        yield put(LoadInboundRoutesSuccess(inbounds));
    } catch (e) {
        yield put(InboundError());
        Logger.error(e.response.data.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* addNewInbound(action: AddInboundRouteAction): any {
    const {
        payload: {
            inbound: { ActionType, Filter, EmailAddress, HttpAddress, Name },
        },
    } = action;
    const FilterType = FilterPresets.EmailAddress;
    const newInbound = {
        Name,
        Filter,
        FilterType,
        ActionType,
        EmailAddress: ActionType === ActionPresets.ForwardToEmail ? EmailAddress : '',
        HttpAddress: ActionType === ActionPresets.NotifyViaHttp ? HttpAddress : '',
    };
    const allInbounds = yield select(getUserInboundRoutes);
    const inboundNames = allInbounds?.map((el) => el.Name);
    try {
        if (inboundNames?.includes(Name)) {
            throw Error('Inbound with given name already exist');
        }
        const addedInbound = yield call(addNewInboundRoute, newInbound);

        yield put(AddNewInboundSuccess([...allInbounds, addedInbound]));
        yield put(CleanSingleInbound());
        yield put(
            ShowToast({
                severity: 'info',
                message: `Route added`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(InboundError());
        const response = e.response?.data ? `Error:${e.response?.data?.Error} ` : e;
        Logger.error(response);
        yield put(
            ShowToast({
                severity: 'error',
                message: `${response}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
function* validateGivenInput(action: ValidateInboundInputAction): any {
    const {
        payload: { name, value, type },
    } = action;

    yield delay(600);
    const singleInbound = yield select(getSingleInbound);
    const inputErrors = yield select(getInboundErrors);
    let updatedErrors = new Set([...inputErrors, name]);
    if ((type === 'email' && isEmail(value)) || (type === 'url' && isURL(value)) || type === 'string') {
        updatedErrors = inputErrors?.filter((el) => el !== name);
        yield put(SetSingleInbound({ ...singleInbound, [name]: value }));
    }

    yield put(SetInputError([...updatedErrors]));
}
function* manageInboundOrder(action: SetInboundOrderAction): any {
    const { payload } = action;
    try {
        const inboundsWithNewOrder = yield call(updateInboundOrder, payload.newOrder);
        yield put(UpdateInboundSuccess(inboundsWithNewOrder));
    } catch (e) {
        yield put(InboundError());
        Logger.error(e.response.data.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* removeInbound(action: DeleteSingleInboundAction): any {
    const { payload } = action;

    try {
        const inboundtoRemove = yield call(deleteInbound, payload.id); //eslint-disable-line
        const allInbounds = yield select(getUserInboundRoutes);
        const updatedInbounds = allInbounds.filter((el) => el.PublicId !== payload.id);
        yield put(UpdateInboundSuccess(updatedInbounds));
        yield put(CleanSingleInbound());
        yield put(
            ShowToast({
                severity: 'info',
                message: `Route deleted`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(InboundError());
        Logger.error(e.response.data.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
function* updateSingleInbound(action: UpdateInboundAction): any {
    const {
        payload: { id, inbound },
    } = action;
    const allInbounds: InboundRoute[] = yield select(getUserInboundRoutes);
    const inboundNames = allInbounds?.map((el) => el.Name);
    const inboundOriginalName = allInbounds.filter((el) => el.PublicId === id)[0].Name;
    const updatedName = inbound.Name;
    const FilterType = FilterPresets.EmailAddress;
    try {
        if (inboundNames?.includes(updatedName) && updatedName !== inboundOriginalName) {
            throw new Error('Inbound with given name already exist.');
        }
        const updatedInbound = yield call(updateInbound, id, { ...inbound, FilterType });
        const newInbounds = allInbounds?.map((el) => {
            if (el.PublicId === id) {
                return updatedInbound;
            }
            return el;
        });
        yield put(UpdateInboundSuccess(newInbounds));
        yield put(CleanSingleInbound());
        yield put(
            ShowToast({
                severity: 'info',
                message: `Route updated`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(InboundError());
        const response = e.response?.data ? `Error:${e.response?.data?.Error} ` : e;
        Logger.error(response);
        yield put(
            ShowToast({
                severity: 'error',
                message: `${response}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchForLoadingInbounds(): any {
    yield takeEvery('@@inbound/INBOUND_LOAD', fetchInbounds);
}
function* watchForAddingNewInbound(): any {
    yield takeEvery('@@inbound/INBOUND_ADD', addNewInbound);
}
function* watchForInputValidation(): any {
    yield takeLatest('@@inbound/INBOUND_VERIFY_INPUT', validateGivenInput);
}
function* watchForChangingOrder(): any {
    yield takeEvery('@@inbound/INBOUND_SET_ORDER', manageInboundOrder);
}
function* watchForDeletetingInbound(): any {
    yield takeEvery('@@inbound/INBOUND_DELETE', removeInbound);
}
function* watchForUpdateingInbound(): any {
    yield takeEvery('@@inbound/INBOUND_UPDATE', updateSingleInbound);
}

const inboundSagas = [
    fork(watchForLoadingInbounds),
    fork(watchForAddingNewInbound),
    fork(watchForInputValidation),
    fork(watchForChangingOrder),
    fork(watchForDeletetingInbound),
    fork(watchForUpdateingInbound),
];

export default inboundSagas;
